import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams, Link } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import { daysOfWeek } from "../Utils/Constants";
import './MyDoctorPage.css';

export default function MyDoctorPage({ doctorId }) {
    const params = useParams();
    const id = doctorId || params.doctorId;
    const API_URL = process.env.REACT_APP_API_URL;
    const DOCTOR_PROFILE_PICTURE_URL = process.env.REACT_APP_STORAGE_DOCTOR_PROFILE_PICTURE_URL;
    axios.defaults.withCredentials = true;
    const [formData, setFormData] = useState({
        full_name: "",
        username: "",
        field_id: "",
        gender: "",
        profile_picture: "",
        brief: "",
        info: "",
        clinic_address: "",
        consultation_fee: "",
        clinic_consultation_fee: 0,
        phone_number: "",
        password: "",
        is_phone: true,
        is_clinic: false,
        open_days: [],
    });
    const [fields, setFields] = useState([]);
    const [message, setMessage] = useState({ type: "", text: "" });
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchDoctor = async () => {
            try {
                const response = await axios.get(`${API_URL}doctor/get-doctor`, {
                });
                setFormData(response.data);
            } catch (err) {
                if (err.response && err.response.status === 401) {
                    navigate("/control/login");
                }
                setMessage({ type: "error", text: "Failed to load doctor information." });
            } finally {
                setLoading(false);
            }
        };

        const fetchFields = async () => {
            try {
                const response = await axios.get(`${API_URL}fields/list`, {
                });
                setFields(response.data);
            } catch (err) {
                if (err.response && err.response.status === 401) {
                    navigate("/control/login");
                }
                setMessage({ type: "error", text: "Failed to load fields information." });
            }
        };

        fetchDoctor();
        fetchFields();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, API_URL]);

    const handleChange = (e) => {
        const { name, checked, value, type, files } = e.target;
    
        setFormData((prevData) => {
            let newFormData = { ...prevData };
    
            if (name === "profile_picture") {
                newFormData.profile_picture = files[0];
            } else if (name === "is_clinic" || name === "is_phone") {
                newFormData[name] = checked;
            } else if (type === "checkbox") {
                // ✅ Fix: Ensure both checking and unchecking work correctly
                newFormData.open_days = checked
                    ? [...prevData.open_days, name] // Add day
                    : prevData.open_days.filter(day => day !== name); // Remove day
            } else {
                newFormData[name] = value;
            }
    
            return newFormData;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage({ type: "", text: "" });

        const formDataToSend = new FormData();
        Object.keys(formData).forEach(key => {
            formDataToSend.append(key, formData[key]);
        });

        try {
            await axios.post(`${API_URL}doctor/update-doctor`, formDataToSend, {
                headers: { 
                    "Content-Type": "multipart/form-data",
                }
            });
            setMessage({ type: "success", text: "Doctor updated successfully!" });
        } catch (err) {
            setMessage({ type: "error", text: "Failed to update doctor: " + err.response.data.message });
        }
    };

    if (loading) {
        return (
            <div className="profile-loading">
                <div className="d-flex flex-column align-items-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading profile...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="doctor-profile-container">
            <div className="container mt-4">
                <div className="row justify-content-center">
                    <div className="col-md-10">
                        <div className="doctor-profile-card">
                            <div className="doctor-profile-header">
                                <h2>{formData.full_name || "Doctor Profile"}</h2>
                                <p className="doctor-profile-subtitle">Manage your professional information</p>
                                <Link to="/control/patients" className="new-patient-btn">
                                    <i className="fas fa-users"></i>
                                    View Patients
                                </Link>
                            </div>
                            <div className="doctor-profile-body">
                                {message.text && (
                                    <div className={`profile-message ${message.type === "error" ? "error" : "success"}`}>
                                        {message.text}
                                    </div>
                                )}
                                <form onSubmit={handleSubmit}>
                                    <div className="profile-form-section">
                                        <h4>Basic Information</h4>
                                        
                                        <div className="profile-picture-section">
                                            {formData.profile_picture && typeof formData.profile_picture === 'string' ? (
                                                <img 
                                                    src={`${process.env.REACT_APP_STORAGE_DOCTOR_PROFILE_PICTURE_URL}${formData.profile_picture}`}
                                                    alt="Profile"
                                                    className="profile-picture-preview"
                                                />
                                            ) : (
                                                <div className="profile-picture-placeholder">
                                                    <i className="fas fa-user"></i>
                                                </div>
                                            )}
                                            <div className="file-input-wrapper">
                                                <input 
                                                    type="file" 
                                                    name="profile_picture" 
                                                    accept="image/*"
                                                    onChange={handleChange} 
                                                    className="file-input-hidden"
                                                    id="profile-picture-input"
                                                />
                                                <label htmlFor="profile-picture-input" className="file-input-button">
                                                    <i className="fas fa-camera"></i>
                                                    Change Photo
                                                </label>
                                            </div>
                                        </div>

                                        <div className="profile-form-group">
                                            <label className="profile-form-label">Username</label>
                                            <div className="profile-form-control" style={{background: '#f8f9fa', padding: '0.75rem'}}>
                                                {formData.username || "Not set"}
                                            </div>
                                        </div>
                                        
                                        <div className="profile-form-group">
                                            <label className="profile-form-label">Full Name</label>
                                            <input 
                                                type="text" 
                                                name="full_name" 
                                                value={formData.full_name || ""} 
                                                onChange={handleChange} 
                                                className="profile-form-control" 
                                                placeholder="Enter your full name"
                                            />
                                        </div>
                                        
                                        <div className="profile-form-group">
                                            <label className="profile-form-label">Brief Description</label>
                                            <textarea 
                                                name="brief" 
                                                value={formData.brief || ""} 
                                                onChange={handleChange} 
                                                className="profile-form-control profile-textarea" 
                                                placeholder="Brief description about yourself"
                                            />
                                        </div>
                                    </div>
                        {formData.profile_picture && (
                            <div className="mb-3">
                                <img 
                                    src={`${DOCTOR_PROFILE_PICTURE_URL}${formData.profile_picture}`} 
                                    alt="Doctor Profile" 
                                    className="img-fluid rounded"
                                    style={{ maxWidth: "150px" }}
                                />
                            </div>
                        )}
                        <div className="mb-3">
                            <label className="form-label">Password (Leave empty if you don't want to change)</label>
                            <input 
                                type="password" 
                                name="password" 
                                onChange={handleChange} 
                                className="form-control" 
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Clinic Consultation Fee</label>
                            <input 
                                type="text" 
                                name="clinic_consultation_fee" 
                                value={formData.clinic_consultation_fee || ""}
                                dir="ltr"
                                onChange={handleChange} 
                                className="form-control" 
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Clinic Address</label>
                            <textarea 
                                name="clinic_address" 
                                value={formData.clinic_address || ""} 
                                onChange={handleChange} 
                                className="form-control" 
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Phone Number</label>
                            <input 
                                type="text" 
                                name="phone_number" 
                                value={formData.phone_number || ""}
                                dir="ltr"
                                onChange={handleChange} 
                                className="form-control" 
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Gender</label>
                            <select 
                                name="gender" 
                                value={formData.gender} 
                                onChange={handleChange} 
                                className="form-control"
                            >
                                <option value="">Select Gender</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                            </select>
                        </div>
                                    <div className="profile-form-section">
                                        <h4>Professional Information</h4>
                                        
                                        <div className="profile-form-group">
                                            <label className="profile-form-label">Specialization</label>
                                            <select 
                                                name="field_id" 
                                                value={formData.field_id || ""} 
                                                onChange={handleChange} 
                                                className="profile-form-control profile-select"
                                            >
                                                <option value="">Select Specialization</option>
                                                {fields.map((field) => (
                                                    <option key={field.field_id} value={field.field_id}>
                                                        {field.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        
                                        <div className="profile-form-group">
                                            <div className="profile-checkbox-group">
                                                <input 
                                                    name="is_clinic" 
                                                    checked={formData.is_clinic || false} 
                                                    onChange={handleChange} 
                                                    type="checkbox"
                                                    className="profile-checkbox"
                                                    id="clinic-booking"
                                                />
                                                <label htmlFor="clinic-booking" className="profile-checkbox-label">
                                                    Enable Clinic Booking
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="profile-form-section">
                                        <h4>Available Days</h4>
                                        <div className="days-of-week-section">
                                            {daysOfWeek.map(({ english, arabic }) => (
                                                <div className={`day-checkbox-group ${formData.open_days.includes(english) ? 'checked' : ''}`} key={english}>
                                                    <input 
                                                        name={english} 
                                                        checked={formData.open_days.includes(english)} 
                                                        onChange={handleChange} 
                                                        type="checkbox"
                                                        className="profile-checkbox"
                                                        id={`day-${english}`}
                                                    />
                                                    <label htmlFor={`day-${english}`} className="profile-checkbox-label">
                                                        {english}
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    <button type="submit" className="update-profile-btn">
                                        <i className="fas fa-save me-2"></i>
                                        Update Profile
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
